import { Component, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { api } from '@src/app/app.const';
import { AppApiService } from '@src/app/core/services/app-api.service';
import { ContactByTypes } from '@src/app/mepco-public/models/contact-by.model';
import { VerificationStatus } from '@src/app/mepco-public/models/verification-status.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface RequestResponse {
  status: string;
  title: string;
  detail: string;
  attempts?: number;
  username?: string;
}

@Component({
  selector: 'app-forgot-username-dialog',
  templateUrl: './forgot-username-dialog.component.html',
  styleUrls: ['./forgot-username-dialog.component.css'],
  standalone: false,
})
export class ForgotUsernameDialogComponent implements OnDestroy {
  contactForm: FormGroup;
  retrieveUserNameForm: FormGroup;
  countdown: number | null = null;
  counterRunning = false;
  private destroy$ = new Subject<void>();

  isLoading = false;
  verificationCodeError = '';

  verificationCodeResponse = signal<RequestResponse | null>(null);
  retrieveUserName = signal<RequestResponse | null>(null);

  emailType = ContactByTypes.EMAIL;
  phoneType = ContactByTypes.PHONE;
  contactSelected = '';

  pending = VerificationStatus.Pending;
  maxAttemptsReached = VerificationStatus.MaxAttemptsReached;
  maxCheckAttemptsReached = VerificationStatus.MaxCheckAttemptsReached;
  multipleAccounts = VerificationStatus.MultipleAccounts;
  error = VerificationStatus.Error;
  failed = VerificationStatus.Failed;
  approved = VerificationStatus.Approved;
  attempts = 0;

  constructor(
    private formBuilder: FormBuilder,
    private apiService: AppApiService,
    private dialogRef: MatDialogRef<ForgotUsernameDialogComponent>,
    private infoSnackBar: MatSnackBar
  ) {
    this.contactForm = this.formBuilder.group({
      contactMethod: [this.phoneType, Validators.required],
      email: ['', [Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]],
      phone: ['', [Validators.maxLength(30)]],
    });

    this.retrieveUserNameForm = this.formBuilder.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.setValidators(this.phoneType);

    this.contactForm
      .get(this.emailType)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          this.verificationCodeResponse()?.status === VerificationStatus.Error &&
          this.contactForm.get(this.emailType)?.valid
        ) {
          this.verificationCodeResponse.set(null);
        }
      });
  }

  onContactMethodChange(contactMethod: string): void {
    this.contactForm.patchValue({
      email: '',
      phone: '',
    });
    this.contactForm.markAsUntouched();

    this.setValidators(contactMethod);
    this.resetResponses();
  }

  private setValidators(contactMethod: string): void {
    const emailControl = this.contactForm.get(this.emailType);
    const phoneControl = this.contactForm.get(this.phoneType);

    if (contactMethod === this.emailType) {
      phoneControl?.clearValidators();
      emailControl?.setValidators([Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]);
      this.contactSelected = this.emailType;
    } else {
      emailControl?.clearValidators();
      phoneControl?.setValidators([Validators.required, Validators.maxLength(30)]);
      this.contactSelected = this.phoneType;
    }

    emailControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
  }

  onSendVerificationCode(stepper: MatStepper): void {
    this.isLoading = true;

    this.sendVerificationCodeRequest()
      .then(data => {
        this.verificationCodeStatus(data);
        if (data.status === this.pending) {
          this.nextStep(stepper);
          this.attempts = data.attempts;
        }
      })
      .catch(error => this.handleRequestError(error, false))
      .finally(() => (this.isLoading = false));
  }

  onResendCode(): void {
    this.isLoading = true;
    this.resetResponses();

    this.sendVerificationCodeRequest()
      .then(data => {
        if (data.status === this.pending) {
          data.detail = 'The verification code has been sent successfully.';
          this.verificationCodeResponse.set(data);
          this.attempts = data.attempts;
        }
      })
      .catch(error => this.handleRequestError(error, false))
      .finally(() => (this.isLoading = false));
  }

  private sendVerificationCodeRequest(): Promise<RequestResponse> {
    return this.apiService.postWithoutToken(
      api.webview.name,
      `user/forgotUsername/sendVerificationCode`,
      { body: this.getFormData() },
      false
    );
  }

  verificationCodeStatus(data: RequestResponse) {
    if (data.status === this.pending) {
      this.verificationCodeResponse.set({
        ...data,
        detail: 'The verification code has been sent successfully.',
      });
    } else if (data.status === this.maxAttemptsReached) {
      this.verificationCodeResponse.set({
        ...data,
        detail: 'Maximum send attempts reached. Please try again later.',
      });
    } else if (data.status === this.multipleAccounts) {
      this.verificationCodeResponse.set({
        ...data,
      });
    } else {
      const codeControl = this.contactForm.get(this.contactSelected);

      if (codeControl) {
        codeControl.setErrors({ notFound: true });
      }
    }
  }

  onRetrieveUsername(stepper: MatStepper): void {
    this.isLoading = true;
    this.resetResponses();

    if (this.retrieveUserName()?.status === this.approved) {
      this.isLoading = false;
      stepper.next();
      return;
    }

    this.getUserNameRequest()
      .then(data => {
        if (data.status === this.approved) {
          this.retrieveUserName.set({
            ...data,
            detail: 'Your username was successfully recovered.',
          });
          this.nextStep(stepper);
        } else if (data.status === this.maxCheckAttemptsReached) {
          this.retrieveUserName.set({
            ...data,
            detail: 'Maximum check attempts reached. Please try again later.',
          });
        } else {
          const codeControl = this.retrieveUserNameForm.get('verificationCode');

          if (codeControl) {
            codeControl.setErrors({ invalidCode: true });
          }
        }
      })
      .catch(error => this.handleRequestError(error, true))
      .finally(() => (this.isLoading = false));
  }

  private getUserNameRequest(): Promise<RequestResponse> {
    const body = {
      ...this.getFormData(),
      verificationCode: this.retrieveUserNameForm.get('verificationCode')?.value,
    };

    return this.apiService.postWithoutToken(api.webview.name, `user/forgotUsername/retrieveUsername`, { body }, false);
  }

  private handleRequestError(error: any, isRetrieve: boolean): void {
    const errorData: RequestResponse = {
      ...error,
      status: this.error,
      attempts: error.attempts || 0,
      detail: error.detail || 'Something went wrong. Please check your data and try again.',
      title: error.title || 'Error',
    };

    if (isRetrieve) {
      this.retrieveUserName.set(errorData);
    } else {
      this.verificationCodeResponse.set(errorData);
    }
  }

  private getFormData(): { phone?: string; email?: string } {
    const contactMethod = this.contactForm.get('contactMethod')?.value;

    if (contactMethod === this.phoneType) {
      return { phone: this.contactForm.get(this.phoneType)?.value };
    } else if (contactMethod === this.emailType) {
      return { email: this.contactForm.get(this.emailType)?.value };
    }
    return {};
  }

  private nextStep(stepper: MatStepper) {
    setTimeout(() => {
      stepper.next();
    }, 2000);
  }

  private resetResponses(): void {
    this.verificationCodeResponse.set(null);
    this.retrieveUserName.set(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.retrieveUserName()?.username).then(
      () => {
        this.infoSnackBar.open('Username copied to clipboard!', '', {
          duration: 3000,
          panelClass: ['copy-text-snackbar'],
        });
      },
      err => {
        this.infoSnackBar.open('Failed to copy text.', '', {
          duration: 3000,
        });
        console.error('Failed to copy text: ', err);
      }
    );
  }
}
